// CoirMats custom-cut builder (from the round-3 design).
// Price: DoormatsUSA parity, coirmats/docs/dusa-rate-snapshot-2026-09-29.md.
// Scene: four doorways built by _src/scenes.py from a wall elevation and a floor
// projected through one defined camera (window.COIR_SCENES); the width picks the doorway. project() here is the
// same function as in scenes.py, so the mat on the floor is exactly to scale.
(function () {
  'use strict';

  const RATES = { '1/2': 10, '5/8': 11, '3/4': 12, '1': 13, '1 1/4': 14 };
  const T_IN = { '1/2': 0.5, '5/8': 0.625, '3/4': 0.75, '1': 1, '1 1/4': 1.25 };
  const LB_SQFT = { '1/2': 1.167, '5/8': 1.25, '3/4': 1.333, '1': 1.429, '1 1/4': 1.733 }; // Itoris weight override
  const T_LABEL = { '1/2': '½″', '5/8': '⅝″', '3/4': '¾″', '1': '1″', '1 1/4': '1¼″' };
  const T_CLEAR = { '1/2': '¾″', '5/8': '⅞″', '3/4': '1″', '1': '1¼″', '1 1/4': '1½″' };
  const T_KEYS = ['1/2', '5/8', '3/4', '1', '1 1/4'];
  const UPCHARGE = { Natural: 0, Chocolate: 1, Black: 1 };
  const TEX = { Natural: 'natural', Chocolate: 'chocolate', Black: 'black' };
  const LIMIT = { a: [12, 528], i: [12, 72] };
  // Width (a) runs side to side along the door, one piece to 44 ft, or to 33 ft in 1¼″ (Arjun, 2026-10-06); length
  // (i) runs from the front of the mat to the door, to 6 ft (the roll's width). Itoris names its fields the same
  // way: Width 1-44 ft, Length 1-6 ft. The thickness's cap decides whether a width can be ordered, not whether it
  // can be typed: a 1¼″ mat wider than 33 ft keeps its size and says so (maxA, render).
  const MAX_A = { '1/2': 528, '5/8': 528, '3/4': 528, '1': 528, '1 1/4': 396 };
  const NAME = { a: 'Width', i: 'Length' };
  const DOORS = ['green', 'navy', 'oxblood', 'clay', 'charcoal'];
  const CFG = window.COIR_SCENES || { scenes: {}, gap: 3 };
  const SCENES = CFG.scenes;
  const GAP = CFG.gap;          // inches between the wall and the mat's back edge
  const U = 8;                  // local px per inch on the mat element (texture: 16 in = 128 px)
  const FRAME = 1.25;           // well frame width, inches
  // The doorway follows the width (Arjun, 2026-10-06): a single door to 4 ft, a double door to 8 ft,
  // a shopfront to 14 ft, a lobby beyond. The width slider gives each rung a quarter of its travel,
  // with a thousand stops a quarter so every half inch can be reached; its top is the thickness's cap.
  const LADDER = [['stoop', 48], ['double', 96], ['shopfront', 168], ['lobby', LIMIT.a[1]]];
  const sceneFor = (a) => (LADDER.find(([k, top]) => SCENES[k] && a <= top) || ['plan'])[0];
  // the lobby picture holds a mat to 33 ft; a wider one is drawn in plan, to scale, under the same lobby rung
  const PICTURE_MAX = 396;
  const knots = (top) => [12, 48, 96, 168, top];  // slider position 0, 1000, 2000, 3000, 4000
  const toSlide = (v, top) => {
    const K = knots(top), n = v > top ? 3 : clamp(K.findIndex((x, j) => v <= K[j + 1]), 0, 3);
    return Math.round(1000 * (n + clamp((v - K[n]) / (K[n + 1] - K[n]), 0, 1)));
  };
  const fromSlide = (p, top) => {
    const K = knots(top), n = Math.min(3, Math.floor(p / 1000));
    return K[n] + ((p - 1000 * n) / 1000) * (K[n + 1] - K[n]);
  };
  const STORE = 'cm-builder-v3';
  // design images live flat in the theme's assets: img/scene/stoop.webp -> <assets>/scene-stoop.webp
  let BASE = '';
  const asset = (p) => (BASE ? p.replace(/^img\/(\w+)\//, BASE + '$1-') : p);
  // Itoris takes sizes in half inches, so a cut is charged on its size rounded down to the half inch.
  // Typed and dragged sizes are already in half inches; this guards a size restored from an old link.
  const charged = (v) => Math.floor(v * 2 + 1e-9) / 2;

  // ---------- helpers ----------
  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => [...root.querySelectorAll(sel)];
  const el = (tag, cls, html) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  };
  // A mat's name comes back from a shared link or saved order: it is shown as text, never parsed as markup.
  const esc = (t) => String(t).replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
  const svgEl = (tag, attrs) => {
    const e = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const k in attrs) e.setAttribute(k, attrs[k]);
    return e;
  };
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  // a link's thickness or colour is looked up only among the builder's own keys, never Object.prototype's
  const own = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
  // a hand-edited link can carry a lone %: such a name is kept as written
  const unname = (t) => { try { return decodeURIComponent(t); } catch (e) { return t; } };
  // a mat restored from a link or a saved order only if the builder could have added it: its own thickness and
  // colour (a colour is made in 5/8 only), a size it cuts in one piece, and the stepper's 1 to 99
  const makeable = (l) => own(RATES, l.t) && own(UPCHARGE, l.c) && (l.c === 'Natural' || l.t === '5/8')
    && l.cutA >= LIMIT.a[0] && l.cutA <= MAX_A[l.t] && l.cutI >= LIMIT.i[0] && l.cutI <= LIMIT.i[1]
    && Number.isInteger(l.qty) && l.qty >= 1 && l.qty <= 99;
  const FRAC = { 0.25: '¼', 0.5: '½', 0.75: '¾' };
  const whole = (rem) => {
    const w = Math.floor(rem + 1e-9), f = FRAC[Math.round((rem - w) * 4) / 4] || '';
    return `${w || !f ? w : ''}${f}`;
  };
  // 38.5 -> 3′ 2½″, 36 -> 3′
  function fmt(inches) {
    const ft = Math.floor(inches / 12 + 1e-9), rest = inches - ft * 12;
    return rest > 1e-6 ? `${ft}′ ${whole(rest)}″` : `${ft}′`;
  }
  const money = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // The live Itoris formula: feet and inch remainders multiplied as floats, rounded up to the cent.
  function priceFor(aIn, iIn, rate) {
    const aFt = Math.floor(aIn / 12), iFt = Math.floor(iIn / 12);
    const length = aFt + (aIn - aFt * 12) / 12;
    const width = iFt + (iIn - iFt * 12) / 12;
    return Math.ceil(length * width * rate * 100) / 100;
  }
  const num = (t) => {
    const s = String(t).replace('½', ' 1/2').replace('¼', ' 1/4').replace('¾', ' 3/4').trim();
    const m = s.match(/^(\d+(?:\.\d+)?)?\s*(?:(\d+)\s*\/\s*(\d+))?$/);
    if (!m || (m[1] == null && m[2] == null)) return NaN;
    return (m[1] ? parseFloat(m[1]) : 0) + (m[2] ? +m[2] / +m[3] : 0);
  };
  // One typed size, in inches: 36 · 3' · 3' 2½" · 3 ft 2 in · 38.5in · 38 1/2 · 3 2
  function parseSize(text) {
    let s = String(text).toLowerCase().replace(/[′’‘`]/g, "'").replace(/[″“”]|''/g, '"').replace(/feet|foot|ft\.?/g, "'").replace(/inches|inch|in\.?/g, '"').trim();
    if (!s) return NaN;
    if (s.includes("'")) {
      const [f, rest] = s.split("'");
      const ft = num(f), inch = rest.replace(/"/g, '').trim() ? num(rest.replace(/"/g, '')) : 0;
      return ft * 12 + inch;
    }
    s = s.replace(/"/g, '').trim();
    const two = s.match(/^(\d+)\s+(\d+(?:\.\d+)?)$/);      // "3 2" reads as 3 ft 2 in
    if (two) return +two[1] * 12 + +two[2];
    return num(s);
  }

  // 3x3 projective transform helpers (row-major) for the mat's matrix3d.
  const adj = (m) => [
    m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
    m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
    m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3],
  ];
  const mm = (a, b) => {
    const c = Array(9);
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
      let s = 0;
      for (let k = 0; k < 3; k++) s += a[3 * i + k] * b[3 * k + j];
      c[3 * i + j] = s;
    }
    return c;
  };
  const mv = (m, v) => [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
  const basis = (p) => {
    const m = [p[0].x, p[1].x, p[2].x, p[0].y, p[1].y, p[2].y, 1, 1, 1];
    const v = mv(adj(m), [p[3].x, p[3].y, 1]);
    return mm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
  };
  // CSS matrix3d mapping a w x h box (origin top-left) onto the quad tl, tr, bl, br.
  function quadMatrix(w, h, tl, tr, bl, br) {
    const src = basis([{ x: 0, y: 0 }, { x: w, y: 0 }, { x: 0, y: h }, { x: w, y: h }]);
    const dst = basis([tl, tr, bl, br]);
    const t = mm(dst, adj(src)).map((v, _, a) => v / a[8]);
    return `matrix3d(${t[0]},${t[3]},0,${t[6]},${t[1]},${t[4]},0,${t[7]},0,0,1,0,${t[2]},${t[5]},0,${t[8]})`;
  }
  // Point X inches right of the door centre, h inches up, d inches out from the wall.
  function project(sc, X, h, d) {
    const s = sc.D / (sc.D - d);
    return { x: sc.mx + X * sc.k0 * s, y: sc.yh + (sc.y0 - sc.yh) * s - h * sc.k0 * s };
  }

  // ---- Itoris bridge. The custom-cut price is charged by Itoris Dynamic Product Options, as on DoormatsUSA.
  // Its fields are found by what they hold, not by ids or labels: two feet selects (1-6 for the side that
  // runs out from the door, 1-44 for the side along it), an inches select after each (0-11.5 in halves),
  // a thickness select (1/2" ... 1 1/4") and a color select (Natural, Chocolate, Black). Itoris calls the
  // 1-6 ft side "Length" and the 1-44 ft side "Width", as the builder does: the builder's width (along the
  // door) always goes to the longer feet field, whatever it is called. A width past that field's last foot
  // (33 until Itoris lists 34-44) is a quote, not a cart line.
  // Itoris may append an option's price to its text ('1/2" +$10.00'); DoormatsUSA strips it with its own script
  const label = (t) => String(t).replace(/\s*\+\$[\d.,]+/g, '').trim();
  const Itoris = {
    find() {
      const form = document.querySelector('[data-itoris-form]');
      const box = document.querySelector('#itoris_dynamicproductoptions');
      if (!form || !box) return null;
      const sels = [...box.querySelectorAll('select')];
      const vals = (sel) => [...sel.options].filter((o) => o.value).map((o) => label(o.textContent));
      const feet = [], inches = [];
      let t = null, c = null;
      sels.forEach((sel, n) => {
        const v = vals(sel);
        if (v.some((x) => /natural|chocolate|black/i.test(x))) c = sel;
        else if (v.some((x) => /\d\s*\/\s*\d|"|″/.test(x))) t = sel;
        else if (v.length && v.every((x) => !isNaN(parseFloat(x)))) {
          const nums = v.map(parseFloat);
          if (nums.some((x) => x % 1)) inches.push({ sel, at: n });
          else feet.push({ sel, at: n, max: Math.max(...nums) });
        }
      });
      if (feet.length !== 2 || inches.length !== 2 || !t || !c) return null;
      const after = (f) => (inches.filter((i) => i.at > f.at).sort((x, y) => x.at - y.at)[0] || {}).sel;
      const along = feet[0].max >= feet[1].max ? feet[0] : feet[1], out = along === feet[0] ? feet[1] : feet[0];
      if (along.max < 33 || out.max > 12) return null;
      return { form, a: [along.sel, after(along)], i: [out.sel, after(out)], t, c, aFt: along.max };
    },
    pick(sel, test) {
      const o = [...sel.options].find((x) => x.value && test(x.textContent.trim()));
      if (!o) return false;
      sel.value = o.value;
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    },
    cart() { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()); },
    remove(key) {
      return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) })
        .then(() => window.CoirCart && window.CoirCart.refresh());
    },
    // Itoris charges a cut by adding a variant it creates at its own price, "(D#…)". It creates one only when its
    // price differs from the product's own $10.00: read before its fields have settled, its price is still
    // $10.00, and it adds the plain $10.00 variant instead and opens the cart, so the check below never runs
    // (launch, 2026-10-05). So Itoris gets the click only once its own price equals the builder's, to the cent.
    async settle(f, each) {
      const dpo = window.dpoOptions, $j = window.jQuery;
      if (!dpo || typeof dpo.updatePrice !== 'function') return false;
      const want = Math.round(each * 100);
      for (let n = 0; n < 20; n++) {
        let got = NaN;
        try { got = Math.round(dpo.updatePrice().current_prices.price * 100); } catch (e) { /* not ready yet */ }
        if (got === want) return true;
        if ($j) [f.a[0], f.a[1], f.i[0], f.i[1], f.t, f.c].forEach((sel) => $j(sel).trigger('change'));
        await new Promise((r) => setTimeout(r, 250));
      }
      return false;
    },
    // set the fields for one mat, submit Itoris's form, and accept the new cart line only at the builder's price
    async add(f, l, each, props) {
      const sizeTo = ([ft, inch], v) => {
        const w = Math.floor(v / 12 + 1e-9), rest = v - w * 12;
        return this.pick(ft, (x) => parseFloat(x) === w) && this.pick(inch, (x) => parseFloat(x) === rest);
      };
      if (Math.floor(charged(l.cutA) / 12 + 1e-9) > f.aFt) throw new Error(`The cart takes widths up to ${f.aFt} ft for now. Get a quote and we will cut this one.`);
      const ok = sizeTo(f.a, charged(l.cutA)) && sizeTo(f.i, charged(l.cutI))
        && this.pick(f.t, (x) => label(x).replace(/["″]/g, '') === l.t)
        && this.pick(f.c, (x) => new RegExp('^' + l.c, 'i').test(label(x)));
      if (!ok) throw new Error('This size is outside what can be ordered online. Get a quote instead.');
      const qty = f.form.querySelector('[data-itoris-qty]');
      if (qty) qty.value = l.qty;
      const box = f.form.querySelector('[data-itoris-props]');
      if (box) box.innerHTML = Object.entries(props).map(([k, v]) => `<input type="hidden" name="properties[${k}]" value="${String(v).replace(/"/g, '&quot;')}">`).join('');
      if (!(await this.settle(f, each))) throw new Error(`The cart could not confirm ${money(each)} for this mat. Get a quote instead.`);
      const before = await this.cart();
      const keys = new Set(before.items.map((x) => x.key));
      // a plain form post would land in a hidden frame, never navigating away
      f.form.target = 'itoris-sink';
      // Itoris hides the form's Add button and puts its own copy (dpo_add2cart) beside it
      const btn = f.form.querySelector('[dpo_add2cart]') || f.form.querySelector('[data-itoris-submit]');
      // Itoris takes over the add from a click on the form's Add button (it listens for the click, not the submit)
      btn.click();
      for (let n = 0; n < 30; n++) {
        await new Promise((r) => setTimeout(r, 400));
        const now = await this.cart();
        const fresh = now.items.filter((x) => !keys.has(x.key) || x.quantity > (before.items.find((b) => b.key === x.key) || {}).quantity);
        if (!fresh.length) continue;
        const want = Math.round(each * 100);
        const line = fresh.find((x) => x.price === want);
        if (!line) {
          await Promise.all(fresh.filter((x) => !keys.has(x.key)).map((x) => this.remove(x.key)));
          throw new Error(`The cart priced this mat differently from ${money(each)}, so it was taken out again. Get a quote and we will confirm the price.`);
        }
        if (window.CoirCart) window.CoirCart.refresh();
        return line.key;
      }
      throw new Error('The cart did not answer. Check your connection and try again, or get a quote.');
    },
  };

  class Builder {
    constructor(root) {
      this.root = root;
      BASE = root.dataset.assets || BASE;
      this.full = root.hasAttribute('data-url');
      this.cart = null;          // the Itoris fields, once they are on the page
      this.busy = false;
      this.s = { scene: 'stoop', mode: 'floor', recess: false, a: 36, i: 24, t: '1/2', c: 'Natural', qty: 1, door: 'green', view: 'scene', zip: '19032' };
      this.err = { a: null, i: null };
      this.order = [];
      this.editing = null;       // the name of the mat taken back to edit, kept for its next Add
      this.shown = null;
      this.scene = null;
      this.layers = {};
      this.readDefaults();
      this.buildStage();
      this.bind();
      this.render(true);
      window.addEventListener('resize', () => this.draw());
      if ('ResizeObserver' in window && this.stage) new ResizeObserver(() => this.draw()).observe(this.stage);
      if (this.full) this.phone();
      this.gallery();
      // a slider is dragged by hand: it moves the mat live and settles on release
      $$(root, '[data-slide]').forEach((r) => {
        r.addEventListener('input', () => this.slide(r, true));
        r.addEventListener('change', () => this.slide(r, false));
      });
      this.connect();
    }

    // Itoris renders its fields after the page loads; wait for them, then switch Add to the real cart
    connect() {
      let n = 0;
      const sink = document.createElement('iframe');
      sink.name = 'itoris-sink';
      sink.hidden = true;
      sink.title = 'Cart';
      document.body.appendChild(sink);
      const look = () => {
        const f = Itoris.find();
        if (f) {
          this.cart = f;
          $$(this.root, '[data-offline]').forEach((e) => (e.hidden = true));
          // a tray kept from an earlier visit keeps only the mats still in the cart
          Itoris.cart().then((c) => {
            const live = new Set(c.items.map((x) => x.key));
            this.order = this.order.filter((l) => !l.key || live.has(l.key));
            this.render();
          });
          return this.render();
        }
        if (++n < 24) return setTimeout(look, 500);
        $$(this.root, '[data-offline]').forEach((e) => (e.hidden = false));
        this.order.forEach((l) => delete l.key);
        this.render();
      };
      look();
    }

    readDefaults() {
      const d = this.root.dataset, s = this.s;
      const q = new URLSearchParams(this.full ? location.search : '');
      let saved = null;
      const legacy = q.has('a') || q.has('l') || q.has('length');   // links made before width and length were swapped
      if (this.full && !q.has('w') && !q.has('mats') && !legacy && !q.has('shot')) {
        try { saved = JSON.parse(localStorage.getItem(STORE) || 'null'); } catch (e) { /* private mode */ }
      }
      if (saved && saved.s) {
        Object.assign(s, saved.s, { qty: 1, view: 'scene' });
        delete s.unit;
        if (!own(RATES, s.t)) s.t = '1/2';
        if (!own(UPCHARGE, s.c)) s.c = 'Natural';
        if (s.c !== 'Natural') s.t = '5/8';
        this.order = (saved.order || []).filter(makeable);
        this.floorOnly();
        return;
      }
      const pick = (k, alt) => q.get(k) ?? alt;
      // w is the width along the door and len the length out from it. Older links named the side along the
      // door "length" (and before that "a"), and the side out from it "width" (or "i").
      const a = parseFloat(q.get('w') ?? q.get('length') ?? pick('a', d.a)), i = parseFloat(q.get('len') ?? (q.has('length') ? q.get('width') : null) ?? pick('i', d.i));
      if (a >= LIMIT.a[0] && a <= LIMIT.a[1]) s.a = Math.round(a * 2) / 2;
      if (i >= LIMIT.i[0] && i <= LIMIT.i[1]) s.i = Math.round(i * 2) / 2;
      if (own(RATES, pick('t', d.t))) s.t = pick('t', d.t);
      if (own(UPCHARGE, pick('c', d.c))) s.c = pick('c', d.c);
      // a link from before the recessed view was visual only: show the well, order the size as typed
      if (pick('m', d.mode) === 'well' || pick('v', '') === 'well') s.recess = true;
      if (DOORS.includes(pick('door', d.door))) s.door = pick('door', d.door);
      // bview, not view: Shopify reserves ?view= for template switching, so an old view=plan link opens the default template
      if (pick('bview', pick('view', '')) === 'plan') s.view = 'plan';
      if (s.c !== 'Natural') s.t = '5/8';
      s.scene = sceneFor(s.a);
      (q.get('mats') || q.get('l') || '').split(';').filter(Boolean).forEach((row) => {
        const [m, la, li, lt, lc, lq, name] = row.split(',');
        // a size is shown as it is charged, to the half inch below
        const a = charged(+la), i = charged(+li);
        const l = { mode: 'floor', a, i, cutA: a, cutI: i, t: lt, c: lc, qty: clamp(Math.round(+lq) || 1, 1, 99), label: unname(name || '').trim() || `Mat ${this.order.length + 1}` };
        if (makeable(l)) this.order.push(l);
      });
    }

    // Recessed is a way to look at the mat, not a choice in the order: state kept from an earlier
    // visit as a well order becomes the same size on the floor, shown recessed.
    floorOnly() {
      const s = this.s;
      if (s.mode === 'well') s.recess = true;
      s.mode = 'floor';
      delete s.deducted;
      delete s.depth;
      if (typeof s.recess !== 'boolean') s.recess = false;
      this.order.forEach((l) => { l.mode = 'floor'; });
    }


    // ---------- stage ----------
    buildStage() {
      const stage = (this.stage = $(this.root, '[data-bd-stage]'));
      if (!stage) return;
      stage.classList.add('bd-stage');
      this.cam = el('div', 'bd-cam');
      this.shadow = el('div', 'bd-shadow');
      this.well = el('div', 'bd-well');
      this.mat = el('div', 'bd-mat');
      this.front = el('div', 'bd-front');
      this.cam.append(this.shadow, this.well, this.mat, this.front);
      this.plan = svgEl('svg', { class: 'bd-plan', 'aria-hidden': 'true' });
      this.lines = svgEl('svg', { class: 'bd-lines', 'aria-hidden': 'true' });
      this.tagA = el('div', 'bd-tag bd-tag--w');
      this.tagI = el('div', 'bd-tag bd-tag--l');
      this.note = el('div', 'bd-note');
      this.sr = el('p', 'sr-only');
      this.sr.setAttribute('role', 'img');
      // Drag zones: the whole of each side edge changes the width (the mat stays centred on the door),
      // the front edge changes the length, and a front corner changes both.
      this.handles = ['l', 'r', 'f', 'fl', 'fr'].map((k) => {
        const h = el('button', 'bd-grip bd-grip--' + k, '<i></i>');
        h.type = 'button';
        h.dataset.grip = k;
        if (k.length === 1) h.setAttribute('role', 'slider');
        h.setAttribute('aria-label', k === 'f' ? 'Length: drag, or use the arrow keys' : k.length === 1 ? 'Width: drag, or use the arrow keys' : 'Width and length: drag the corner, or use the arrow keys');
        return h;
      });
      stage.append(this.cam, this.plan, this.lines, this.tagA, this.tagI, this.note, this.sr, ...this.handles);
      stage.setAttribute('aria-label', 'Your mat, drawn to scale at the door');
      this.bindDrag();
    }

    layer(k) {
      if (this.layers[k]) return this.layers[k];
      const sc = SCENES[k], layer = el('div', 'bd-layer');
      layer.style.cssText = `width:${sc.w}px;height:${sc.h}px`;
      const img = new Image();
      img.alt = '';
      img.decoding = 'async';
      img.src = asset(sc.src);
      layer.appendChild(img);
      if (sc.recolour) {
        const r = sc.doorRect, door = new Image();
        door.alt = '';
        door.className = 'bd-door';
        door.style.cssText = `left:${r[0]}px;top:${r[1]}px;width:${r[2] - r[0]}px;height:${r[3] - r[1]}px`;
        layer.appendChild(door);
        layer.door = door;
      }
      this.cam.insertBefore(layer, this.shadow);
      return (this.layers[k] = layer);
    }

    // ---------- model ----------
    get cut() { return { a: this.s.a, i: this.s.i }; }
    get rate() { return RATES[this.s.t] + UPCHARGE[this.s.c]; }
    get valid() { return !this.err.a && !this.err.i && !this.over; }
    // the most a size can be in the chosen thickness
    hi(k) { return k === 'a' ? MAX_A[this.s.t] : LIMIT.i[1]; }
    // a width the thickness is not cut to: 1¼″ past 33 ft, set before 1¼″ was chosen, or typed, or from a link
    get over() { return this.s.a > this.hi('a'); }
    linePrice(l) { return priceFor(charged(l.cutA), charged(l.cutI), RATES[l.t] + UPCHARGE[l.c]); }
    f(v) { return fmt(v); }
    // write a size into its boxes
    fill(k, force) {
      const v = this.s[k], ft = Math.floor(v / 12 + 1e-9), act = document.activeElement;
      // while one of this size's boxes is being typed in, its partner is left alone: 4 ft + "14" stays 4 and 14 until the entry is finished
      if (!force && act && act.dataset && (act.dataset.size || act.dataset.ft || act.dataset.in) === k) return;
      const set = (sel, val) => { const f = $(this.root, sel); if (f) f.value = val; };
      set(`[data-ft="${k}"]`, ft);
      set(`[data-in="${k}"]`, whole(v - ft * 12));
    }
    // move a size's slider to it; the one under the hand is left alone
    slider(k) {
      const r = $(this.root, `[data-slide="${k}"]`);
      if (!r || r === this.sliding) return;
      r.value = k === 'a' ? toSlide(this.s[k], this.hi(k)) : this.s[k];
      r.setAttribute('aria-valuetext', fmt(this.s[k]));
      r.style.setProperty('--p', ((r.value - r.min) / (r.max - r.min) * 100).toFixed(2) + '%');
    }
    // a slider roughs the size in, in half inches like the boxes
    slide(r, live) {
      const k = r.dataset.slide, v = Math.round((k === 'a' ? fromSlide(+r.value, this.hi(k)) : +r.value) * 2) / 2;
      this.sliding = live ? r : null;
      this.err[k] = this.route = this.rounded = null;
      this.swapped = false;
      this.s[k] = clamp(v, LIMIT[k][0], this.hi(k));
      r.setAttribute('aria-valuetext', fmt(this.s[k]));
      r.style.setProperty('--p', ((r.value - r.min) / (r.max - r.min) * 100).toFixed(2) + '%');
      this.tidy = true;
      this.render(false, live);
      this.tidy = false;
    }
    // read a typed size back, in inches
    typed(k) {
      const g = (sel) => { const f = $(this.root, sel); return f ? f.value.trim() : ''; };
      const ft = g(`[data-ft="${k}"]`), inch = g(`[data-in="${k}"]`).replace(/["″”]|in\.?$/gi, '').trim();
      if (!ft && !inch) return NaN;
      return (ft ? num(ft) : 0) * 12 + (inch ? num(inch) : 0);
    }

    setDim(k, inches, source) {
      const [lo, hi] = LIMIT[k];
      this.route = null;
      if (isNaN(inches)) {
        this.err[k] = 'Enter feet and inches as numbers, such as 3 ft 2.5 in.';
        return;
      }
      const rounded = Math.round(inches * 2) / 2;
      this.swapped = false;
      if (rounded < lo) {
        this.err[k] = `${NAME[k]} starts at ${this.f(lo)}.`;
        return;
      }
      // The roll is 6 ft wide, so one side has to fit it. A length typed over 6 ft with a width that
      // fits the roll is the same cut turned round: make the long side the width and say so.
      if (k === 'i' && rounded > hi && rounded <= LIMIT.a[1] && this.s.a <= hi && source === 'type') {
        this.s.i = this.s.a;
        this.s.a = rounded;
        this.err.a = this.err.i = null;
        this.swapped = true;
        this.rounded = null;
        return;
      }
      if (rounded > hi) {
        // limits that route: an oversize mat is a quote, not a dead end
        this.err[k] = k === 'a' ? `One piece runs up to 44 ft wide.` : `One piece runs up to 6 ft long: the coir comes off a 6 ft wide roll.`;
        this.route = { k, want: rounded };
        return;
      }
      this.err[k] = null;
      this.s[k] = rounded;
      this.rounded = source === 'type' && Math.abs(rounded - inches) > 1e-6 ? k : this.rounded === k ? null : this.rounded;
    }

    // ---------- events ----------
    bind() {
      const r = this.root, s = this.s;
      const SIZE_BOX = '[data-ft],[data-in]';
      const sizeKey = (t) => t.dataset.ft || t.dataset.in;
      r.addEventListener('click', (e) => {
        const b = e.target.closest('button,[data-share],[data-quote]');
        if (!b || !r.contains(b) || b.disabled || b.dataset.grip) return;
        const d = b.dataset;
        if (d.step) {
          const [k, by] = d.step.split(',');
          this.err[k] = null;
          this.setDim(k, s[k] + +by, 'step');
        } else if ('fit' in d) {
          // the one-tap answer to a 1¼″ mat past 33 ft: cut it at 33 ft
          s.a = this.hi('a');
          this.err.a = this.route = this.rounded = null;
          this.tidy = true;
        } else if ('recess' in d) {
          s.recess = !s.recess;
        } else if (d.quick) {
          // a common size, written length x width
          const [i, a] = d.quick.split(',').map(Number);
          Object.assign(s, { a, i, view: 'scene' });
          this.err.a = this.err.i = this.rounded = this.route = null;
          this.swapped = false;
          this.tidy = true;
        } else if (d.t) {
          if (b.getAttribute('aria-disabled') === 'true') return;
          s.t = d.t;
        } else if (d.c) {
          s.c = d.c;
          // colour comes first: a colour made in one thickness sets that thickness, and says so on the chips
          if (d.c !== 'Natural') s.t = '5/8';
        } else if (d.door) s.door = d.door;
        else if (d.view) s.view = d.view;
        else if (d.qty) s.qty = clamp(s.qty + +d.qty, 1, 99);
        else if ('add' in d) return this.addLine();
        else if ('another' in d) { const f = $(r, '[data-ft="a"]'); if (f) { f.scrollIntoView({ block: 'center', behavior: 'smooth' }); f.focus({ preventScroll: true }); } return; }
        else if ('remove' in d) { const l = this.order.splice(+d.remove, 1)[0]; if (l && l.key) Itoris.remove(l.key); }
        else if ('checkout' in d) return this.checkout();
        else if ('edit' in d) {
          const l = this.order.splice(+d.edit, 1)[0];
          if (l && l.key) Itoris.remove(l.key);
          Object.assign(s, { a: l.cutA, i: l.cutI, t: l.t, c: l.c, qty: l.qty, recess: !!l.recess });
          this.editing = l.label;
          this.tidy = true;
        } else if ('share' in d) return this.share(b);
        else if ('quote' in d) return this.quote();
        else if ('quoteClose' in d) return this.quote(false);
        else if ('print' in d) return window.print();
        else return;
        this.render();
        this.tidy = false;
      });
      r.addEventListener('change', (e) => {
        const t = e.target;
        if (t.matches(SIZE_BOX)) {
          const k = sizeKey(t);
          this.setDim(k, this.typed(k), 'type');
          this.tidy = true;             // the finished entry is rewritten tidily: 3 ft 14 in -> 4 ft 2 in
          this.render();
          this.tidy = false;
        } else if (t.matches('[data-zip]')) {
          const z = t.value.replace(/\D/g, '').slice(0, 5);
          if (z.length === 5) s.zip = z;
          this.render();
        }
      });
      r.addEventListener('input', (e) => {
        const t = e.target;
        if (!t.matches(SIZE_BOX)) return;
        const k = sizeKey(t), v = Math.round(this.typed(k) * 2) / 2;
        if (!(v >= LIMIT[k][0] && v <= LIMIT[k][1])) return;      // mid-word or out of range: wait for the finished entry
        this.err[k] = null;
        this.route = null;
        s[k] = v;
        this.render(false, true);
      });
      r.addEventListener('keydown', (e) => {
        const t = e.target;
        if (!t.matches(SIZE_BOX + ',[data-zip]')) return;
        if (e.key === 'Enter') return t.blur();
        // arrow keys nudge: half an inch in an inches box, a foot in the feet box
        const dir = { ArrowUp: 1, ArrowDown: -1 }[e.key];
        if (!dir || t.matches('[data-zip]')) return;
        e.preventDefault();
        const k = sizeKey(t);
        this.err[k] = null;
        this.setDim(k, clamp(s[k] + dir * (t.dataset.ft ? 12 : 0.5), LIMIT[k][0], Math.max(this.hi(k), s[k])), 'step');
        this.tidy = true;
        this.render();
        this.tidy = false;
        t.select();
      });
      r.addEventListener('focusin', (e) => e.target.matches(SIZE_BOX) && e.target.select && e.target.select());
      $$(r, '[data-save]').forEach((f) => f.addEventListener('submit', (e) => {
        e.preventDefault();
        const o = $(f, '[data-save-done]');
        if (o) o.hidden = false;
      }));
    }

    // Direct manipulation. The camera holds still for the whole drag, so the edge you took hold of
    // stays under the finger; the size is how far the pointer has moved across the floor, snapped
    // to the half inch. On release the camera eases to the fit for the new size.
    bindDrag() {
      const stage = this.stage;
      const snap = (v) => Math.round(v * 2) / 2;
      const at = (e) => {
        const r = stage.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };
      // pointer -> inches on the floor under camera c: X right of the door centre, d out from the wall.
      // `depth` fixes the line the pointer is read along (a side edge is read at the mat's middle).
      const floor = (p, c, depth) => {
        if (c.plan) return { X: (p.x - c.cx) / c.sp, d: (p.y - c.wallY) / c.sp };
        const sc = SCENES[this.scene], h = this.s.recess ? 0 : T_IN[this.s.t];
        const ix = (p.x - c.tx) / c.z, iy = (p.y - c.ty) / c.z;
        const sd = Math.max(0.5, (iy - sc.yh) / (sc.y0 - sc.yh - h * sc.k0));
        const s = depth == null ? sd : sc.D / (sc.D - depth);
        return { X: (ix - sc.mx) / (sc.k0 * s), d: sc.D * (1 - 1 / sd) };
      };
      this.handles.forEach((h) => {
        const k = h.dataset.grip, both = k.length === 2, side = k.includes('l') ? -1 : 1;
        h.addEventListener('pointerdown', (e) => {
          if (e.button > 0 || !this.camera || this.hold) return;
          e.preventDefault();
          try { h.setPointerCapture(e.pointerId); } catch (err) { /* synthetic pointer */ }
          const s = this.s, c = { ...this.camera }, W = stage.clientWidth, H = stage.clientHeight;
          const a0 = s.a, i0 = s.i, p0 = at(e);
          const front = (i) => GAP + i, middle = GAP + i0 / 2;
          const f0 = floor(p0, c, both ? front(i0) : middle);
          // a drag stops where the mat would leave the picture; let go and the view steps back
          const maxI = Math.max(i0, Math.floor((floor({ x: 0, y: H - 16 }, c).d - GAP) * 2) / 2);
          const maxA = (i) => {
            const line = both || k === 'f' ? front(i) : middle;
            return Math.max(a0, Math.floor(Math.min(floor({ x: W - 12, y: 0 }, c, line).X, -floor({ x: 12, y: 0 }, c, line).X) * 4) / 2);
          };
          this.hold = c;
          this.lock = this.scene;
          h.classList.add('is-on');
          stage.classList.add('is-dragging');
          let last = p0, raf = 0;
          const tick = () => {
            raf = 0;
            let a = a0, i = i0;
            if (k === 'f' || both) i = clamp(snap(i0 + floor(last, c).d - f0.d), LIMIT.i[0], Math.min(LIMIT.i[1], maxI));
            if (k !== 'f') a = clamp(snap(a0 + 2 * side * (floor(last, c, both ? front(i) : middle).X - f0.X)), LIMIT.a[0], Math.min(Math.max(this.hi('a'), a0), maxA(i)));
            if (a === s.a && i === s.i) return;
            s.a = a;
            s.i = i;
            this.err.a = this.err.i = this.route = this.rounded = null;
            this.swapped = false;
            this.render(false, true);
          };
          const move = (ev) => {
            last = at(ev);
            if (!raf) raf = requestAnimationFrame(tick);
          };
          const up = () => {
            if (raf) { cancelAnimationFrame(raf); tick(); }
            h.removeEventListener('pointermove', move);
            h.removeEventListener('pointerup', up);
            h.removeEventListener('pointercancel', up);
            h.classList.remove('is-on');
            stage.classList.remove('is-dragging');
            const from = this.hold, was = this.scene;
            this.hold = this.lock = null;
            this.render();
            this.ease(from, was);
          };
          h.addEventListener('pointermove', move);
          h.addEventListener('pointerup', up);
          h.addEventListener('pointercancel', up);
        });
        // a finger on a grip resizes the mat; it never scrolls the page
        h.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
        h.addEventListener('keydown', (e) => {
          const key = { ArrowRight: ['a', 1], ArrowLeft: ['a', -1], ArrowDown: ['i', 1], ArrowUp: ['i', -1] }[e.key];
          if (!key) return;
          e.preventDefault();
          // on an edge every arrow moves that edge; on a corner left/right is the width, up/down the length
          const d = both ? key[0] : k === 'f' ? 'i' : 'a';
          const sign = both || k === 'f' ? key[1] : k === 'l' ? -key[1] : key[1];
          this.err[d] = null;
          this.setDim(d, clamp(this.s[d] + sign * 0.5, LIMIT[d][0], Math.max(this.hi(d), this.s[d])), 'step');
          this.render();
        });
      });
    }

    // after a drag: ease the camera from where it was held to the fit for the new size
    ease(from, scene) {
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('shot');
      cancelAnimationFrame(this.camRaf);
      this.blend = null;
      if (!from || scene !== this.scene || still) return this.draw();
      const t0 = performance.now();
      const step = (now) => {
        const p = clamp((now - t0) / 360, 0, 1);
        this.blend = p < 1 ? { from, e: 1 - Math.pow(1 - p, 3) } : null;
        this.draw();
        if (p < 1) this.camRaf = requestAnimationFrame(step);
      };
      this.blend = { from, e: 0 };
      this.draw();
      this.camRaf = requestAnimationFrame(step);
    }

    // Phones: the scene stays pinned to the top third (CSS) and the price bar to the bottom. The bar steps
    // aside only while the panel's own Add to cart is on screen, so the two are never both in view.
    phone() {
      const bar = $(this.root, '.sbar--bld'), row = $(this.root, '.total .buy-row');
      if (bar && row && 'IntersectionObserver' in window) new IntersectionObserver(([en]) => bar.classList.toggle('off', en.isIntersecting)).observe(row);
    }

    addLine() {
      if (!this.valid || this.busy) return;
      const c = this.cut;
      const line = { ...this.s, cutA: c.a, cutI: c.i, label: this.editing || `Mat ${this.order.length + 1}` };
      const done = () => {
        this.order.push(line);
        if (this.editing === line.label) this.editing = null;
        this.s.qty = 1;
        this.render();
        const tray = $(this.root, '[data-order-wrap]');
        if (tray && tray.scrollIntoView) tray.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      };
      if (!this.cart) return done();
      // with Itoris on the page the mat goes into the real cart, priced by Itoris, checked to the cent
      this.busy = true;
      this.status('Adding to the cart…');
      Itoris.add(this.cart, line, this.linePrice(line), this.lineProps(line))
        .then((key) => { line.key = key; this.status(''); done(); })
        .catch((err) => this.status(err.message, true))
        .finally(() => { this.busy = false; });
    }

    // what the order reads besides Itoris's own size, thickness and color fields: the builder link, hidden
    lineProps() {
      return { _Builder: this.link() };
    }

    status(text, bad) {
      $$(this.root, '[data-o="cartstatus"]').forEach((e) => { e.textContent = text; e.hidden = !text; e.classList.toggle('is-bad', !!bad); });
    }

    checkout() {
      if (this.cart && this.order.some((l) => l.key)) { location.href = this.root.dataset.cart || '/cart'; return; }
      // no Itoris yet: the order goes to the Commercial desk as a quote request
      const text = this.order.map((l) => `${l.qty} × ${l.label}: ${this.lineText(l)}`).join('\n') + `\nBuilder link: ${this.link()}`;
      location.href = `${this.root.dataset.commercial || ''}?quote=${encodeURIComponent(text)}#quote`;
    }


    link(page) {
      const s = this.s;
      const q = new URLSearchParams({ w: s.a, len: s.i, t: s.t, c: s.c });
      if (s.recess) q.set('v', 'well');
      if (s.door !== 'green') q.set('door', s.door);
      if (s.view === 'plan') q.set('bview', 'plan');
      if (this.order.length) q.set('mats', this.order.map((l) => ['f', l.cutA, l.cutI, l.t, l.c, l.qty, encodeURIComponent(l.label)].join(',')).join(';'));
      return (page || location.origin + location.pathname) + '?' + q.toString();
    }

    share(btn) {
      const url = this.link();
      const done = () => {
        const o = btn.querySelector('[data-share-label]') || btn;
        const old = o.textContent;
        o.textContent = 'Link copied';
        setTimeout(() => (o.textContent = old), 1800);
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(url).then(done, done);
      else {
        const ta = el('textarea');
        ta.value = url;
        ta.style.cssText = 'position:fixed;left:-999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) { /* shown below instead */ }
        ta.remove();
        done();
      }
      const out = $(this.root, '[data-o="link"]');
      if (out) { out.textContent = url; out.hidden = false; }
    }

    lineText(l) {
      const sq = (l.cutA / 12) * (l.cutI / 12);
      const rate = RATES[l.t] + UPCHARGE[l.c];
      return `Length ${this.f(l.cutI)} × Width ${this.f(l.cutA)} · ${T_LABEL[l.t]} · ${l.c} · ${sq.toFixed(2)} sq ft @ ${money(rate)}`;
    }

    quoteLines() {
      if (this.order.length) return this.order;
      const c = this.cut;
      return [{ ...this.s, cutA: c.a, cutI: c.i, label: 'Mat 1' }];
    }

    quote(open = true) {
      const m = $(this.root, '[data-quote-modal]');
      if (!m) return;
      if (!open) { m.hidden = true; document.documentElement.classList.remove('quote-open'); return; }
      const rows = this.quoteLines();
      const total = rows.reduce((s, l) => s + this.linePrice(l) * l.qty, 0);
      const today = new Date();
      const until = new Date(today.getTime() + 30 * 864e5);
      const df = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const id = 'CQ-' + today.toISOString().slice(2, 10).replace(/-/g, '') + '-' + String(Math.round(total * 100) % 997).padStart(3, '0');
      $(m, '[data-quote-body]').innerHTML = `
        <div class="q-meta"><div><span class="cap">Quote</span><b>${id}</b></div><div><span class="cap">Date</span><b>${df(today)}</b></div><div><span class="cap">Valid until</span><b>${df(until)}</b></div></div>
        <table class="q-table"><thead><tr><th>Mat</th><th>Cut size and spec</th><th class="num">Qty</th><th class="num">Each</th><th class="num">Line</th></tr></thead><tbody>
        ${rows.map((l) => `<tr><td><b>${esc(l.label)}</b></td><td>${this.lineText(l)}</td><td class="num">${l.qty}</td><td class="num">${money(this.linePrice(l))}</td><td class="num">${money(this.linePrice(l) * l.qty)}</td></tr>`).join('')}
        </tbody><tfoot><tr><td colspan="4">Mats subtotal</td><td class="num">${money(total)}</td></tr><tr><td colspan="4">Shipping to ${this.s.zip}</td><td class="num">${this.shipText(rows)}</td></tr></tfoot></table>
        <p class="q-terms">Vinyl-backed coir, cut to order. Ships the next business day. Shipping is calculated by size and ZIP; rolls and large orders travel by freight, quoted before you pay. Custom cuts can be returned within 30 days with a 15% restocking fee. Business accounts pay by card or prepaid purchase order.</p>
        <p class="q-link"><span class="cap">Reorder link</span>${this.link()}</p>`;
      m.hidden = false;
      document.documentElement.classList.add('quote-open');
    }

    // shipping: calculated by weight and ZIP (site.js holds the estimator both pages share). A cut's weight is the
    // one Itoris gives the cart line, whole pounds of its area times the thickness's weight a square foot
    // (docs/itoris-custom-cut.md); a cart past 150 lb has no UPS rate and goes by freight.
    ship(rows) {
      const S = window.CoirShip;
      if (!S) return null;
      const lbs = rows.reduce((n, l) => n + Math.ceil((l.cutA / 12) * (l.cutI / 12) * LB_SQFT[l.t]) * l.qty, 0);
      return S.estimate(this.s.zip, lbs);
    }
    shipText(rows) {
      const e = this.ship(rows);
      return !e ? 'Shown at checkout' : e.freight ? 'Freight, quoted before you pay' : e.quote ? 'Quoted by email' : `about ${money(e.cost)}`;
    }

    // ---------- render ----------
    render(first, live) {
      const r = this.root, s = this.s;
      const out = (k, v) => $$(r, `[data-o="${k}"]`).forEach((e) => (e.textContent = v));
      const cut = this.cut;
      const sqft = (cut.a / 12) * (cut.i / 12);
      const each = priceFor(charged(cut.a), charged(cut.i), this.rate);
      r.classList.toggle('is-invalid', !this.valid);

      ['a', 'i'].forEach((k) => {
        if (!this.err[k]) this.fill(k, this.tidy);
        this.slider(k);
        const box = $(r, `[data-field="${k}"]`);
        const over = k === 'a' && !this.err.a && this.over;
        if (box) box.classList.toggle('has-error', !!this.err[k] || over);
        const e = $(r, `[data-err="${k}"]`);
        if (e) {
          const route = this.route && this.route.k === k;
          const quote = (want) => `<a href="${this.root.dataset.commercial || ''}?size=${k === 'a' ? want : s.a}x${k === 'i' ? want : s.i}#quote">`;
          e.innerHTML = this.err[k] ? `${this.err[k]}${route ? ` ${quote(this.route.want)}We can seam two pieces. Get a quote</a>` : ''}`
            : over ? `${T_LABEL[s.t]} is cut up to ${this.hi('a') / 12} ft wide in one piece. <button type="button" class="dim-fit" data-fit>Make it ${this.hi('a') / 12} ft wide</button>, choose ½″ to 1″ (to 44 ft), or ${quote(s.a)}get a quote for two pieces</a>.`
            : this.rounded === k ? `Rounded to the nearest ½″: ${this.f(s[k])}.` : this.swapped && k === 'i' ? 'Length runs to 6 ft, so the long side is now the width. It is the same cut.' : '';
          e.classList.toggle('is-note', !this.err[k] && !over);
        }
      });

      // while a drag or a keystroke is in flight only the numbers and the picture change
      if (!live) {
      const press = (sel, key, val) => $$(r, sel).forEach((b) => b.tagName === 'BUTTON' && b.setAttribute('aria-pressed', String(b.dataset[key] === String(val))));
      $$(r, '[data-recess]').forEach((b) => b.setAttribute('aria-pressed', String(s.recess)));
      press('[data-c]', 'c', s.c);
      press('[data-door]', 'door', s.door);
      $$(r, '[data-t]').forEach((b) => {
        const ok = s.c === 'Natural' || b.dataset.t === '5/8';
        b.setAttribute('aria-pressed', String(b.dataset.t === s.t));
        b.setAttribute('aria-disabled', String(!ok));
        b.title = ok ? '' : `${s.c} is made in ⅝″ only`;
      });
      const tnote = s.c !== 'Natural' ? `${s.c} is made in ⅝″ only. Natural comes in all five.`
        : s.t === '1 1/4' ? '1¼″ is cut up to 33 ft wide in one piece; ½″ to 1″ run to 44 ft.' : '';
      out('tnote', tnote);
      $$(r, '[data-o="tnote"]').forEach((e) => (e.hidden = !tnote));
      const zip = $(r, '[data-zip]');
      if (zip && document.activeElement !== zip) zip.value = s.zip;
      this.profile();
      }

      out('maxa', this.f(this.hi('a')));
      out('maxaft', `${this.hi('a') / 12} ft`);
      out('across', this.f(s.a));
      out('into', this.f(s.i));
      out('size', `${this.f(s.i)} L × ${this.f(s.a)} W`);
      $$(r, '[data-quick]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.quick === `${s.i},${s.a}`)));
      out('t', T_LABEL[s.t]);
      out('c', s.c);
      out('price', this.valid ? money(each) : '—');
      out('total', this.valid ? money(each * s.qty) : '—');
      out('qty', s.qty);
      out('math', `${sqft.toFixed(2)} sq ft × ${money(this.rate)}`);
      out('clear', T_CLEAR[s.t]);
      out('cartline', this.lineText({ ...s, cutA: cut.a, cutI: cut.i }));
      const here = [{ ...s, cutA: cut.a, cutI: cut.i }];
      const est = this.ship(here);
      out('ship', !est ? '' : est.freight ? 'Travels by freight, quoted before you pay' : est.quote ? 'No UPS Ground to this ZIP; we quote shipping by email' : `Shipping about ${money(est.cost)}`);
      out('eta', window.CoirShip ? window.CoirShip.eta(s.zip) : '');
      $$(r, '[data-add]').forEach((b) => (b.disabled = !this.valid));

      // order tray
      const list = !live && $(r, '[data-order]');
      if (list) {
        $$(r, '[data-href]').forEach((a) => (a.href = this.link(a.dataset.href).split('&mats=')[0]));
        const total = this.order.reduce((sum, l) => sum + this.linePrice(l) * l.qty, 0);
        list.innerHTML = this.order.map((l, n) => {
          const k = 46 / Math.max(l.cutA, l.cutI * 1.6, 30);
          return `<li class="ol"><span class="ol-pic"><i class="tex-${TEX[l.c]}" style="width:${Math.max(8, l.cutA * k).toFixed(1)}px;height:${Math.max(6, l.cutI * k).toFixed(1)}px"></i></span>
            <div class="ol-main"><div class="ol-top"><b>${esc(l.label)}</b><span>${money(this.linePrice(l) * l.qty)}</span></div>
            <p>${l.qty > 1 ? l.qty + ' × ' : ''}${this.f(l.cutI)} long × ${this.f(l.cutA)} wide · ${T_LABEL[l.t]} · ${l.c}</p>
            <div class="ol-act"><button type="button" data-edit="${n}">Edit</button><button type="button" data-remove="${n}">Remove</button></div></div></li>`;
        }).join('');
        out('ordertotal', money(total));
        out('ordercount', this.order.length === 1 ? '1 mat' : `${this.order.length} mats`);
        $$(r, '[data-order-wrap]').forEach((e) => (e.hidden = !this.order.length));
        out('shipnote', this.order.length ? `Shipping to ${s.zip}: ${this.shipText(this.order)}. Confirmed at checkout.` : '');
        out('addlabel', !this.cart ? (this.order.length ? 'Add this mat too' : 'Add to order') : this.order.length ? 'Add this mat too' : 'Add to cart');
        out('checkoutlabel', this.cart ? 'Checkout' : 'Get a quote for these');
        // business tools open themselves for a multi-mat or large order
        const tools = $(r, '[data-tools]');
        if (tools && (this.order.length > 1 || total >= 500) && !this.toolsShown) { tools.open = true; this.toolsShown = true; }
        if (this.full && !document.documentElement.classList.contains('shot')) {
          try { localStorage.setItem(STORE, JSON.stringify({ s, order: this.order })); } catch (e) { /* private mode */ }
        }
      }

      // stage
      if (!this.stage) return;
      this.target = { a: cut.a, i: cut.i, t: T_IN[s.t] };
      const next = sceneFor(cut.a);
      s.scene = next === 'plan' ? s.scene : next;
      const wide = cut.a > PICTURE_MAX;
      const view = this.lock || (next === 'plan' || s.view === 'plan' || wide ? 'plan' : next);      // a drag never changes the doorway under the hand
      $$(r, '[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === (view === 'plan' ? 'plan' : 'scene'))));
      // the ladder marks the doorway this width calls for, live, even while a drag holds the picture still
      $$(r, '[data-rung]').forEach((e) => e.classList.toggle('is-on', e.dataset.rung === s.scene));
      this.photos(live);
      if (view !== this.scene) {
        this.scene = view;
        this.stage.dataset.scene = view;
        if (view !== 'plan') this.layer(view);
        Object.keys(this.layers).forEach((k) => this.layers[k].classList.toggle('on', k === view));
        if (!first) {
          // a short ease so the change of doorway reads as a move, not a cut
          this.stage.classList.add('is-switching');
          clearTimeout(this.swT);
          this.swT = setTimeout(() => this.stage.classList.remove('is-switching'), 300);
        }
      }
      const sc = SCENES[view];
      const lay = this.layers[view];
      if (lay && lay.door && lay.door.dataset.c !== s.door) {
        lay.door.dataset.c = s.door;
        lay.door.src = asset(sc.doors[s.door]);
      }
      this.stage.dataset.colour = s.c;
      this.stage.dataset.mode = s.recess ? 'well' : 'floor';
      this.stage.dataset.paint = sc && sc.recolour ? '1' : '0';
      this.mat.className = 'bd-mat tex-' + TEX[s.c];
      this.front.className = 'bd-front edge-' + TEX[s.c];
      if (!this.lock) this.note.textContent = view === 'plan' ? (wide && s.view !== 'plan' ? 'Plan view, to scale: wider than 33 ft, past the lobby picture' : 'Plan view, to scale') : sc.note;
      this.sr.textContent = `${s.recess ? 'Mat shown in a recessed well' : 'Mat on the floor'} at ${view === 'plan' ? 'the door, in plan' : 'a ' + sc.label.toLowerCase()}, ${this.f(cut.i)} long by ${this.f(cut.a)} wide, ${T_LABEL[s.t]} thick ${s.c.toLowerCase()} coir, ${sqft.toFixed(2)} square feet, ${this.valid ? money(each) : 'size out of range'}.`;
      this.tagA.innerHTML = `<span>Width</span><b>${this.f(cut.a)}</b>`;
      this.tagI.innerHTML = `<span>Length</span><b>${this.f(cut.i)}</b>`;
      this.handles.filter((h) => h.dataset.grip.length === 1).forEach((h) => {
        const k = h.dataset.grip === 'f' ? 'i' : 'a';
        h.setAttribute('aria-valuenow', s[k]);
        h.setAttribute('aria-valuetext', this.f(s[k]));
        h.setAttribute('aria-valuemin', LIMIT[k][0]);
        h.setAttribute('aria-valuemax', Math.max(this.hi(k), s[k]));
      });

      const still = live || window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('shot');
      if (first || still || !this.shown) {
        cancelAnimationFrame(this.raf);
        this.shown = { ...this.target };
        this.draw();
      } else this.tween();
    }

    // Thickness you can see: a true-scale section of pile and backing, with the door edge above it.
    profile() {
      const box = $(this.root, '[data-profile]');
      if (!box) return;
      const t = T_IN[this.s.t], k = 40, W = 330, floor = 96, x0 = 12, mw = 150, well = this.s.recess;
      const top = floor - t * k, back = 0.12 * k, door = top - 0.25 * k;
      const pile = this.s.c === 'Natural' ? ['#D2A662', '#A47A36'] : this.s.c === 'Black' ? ['#3A3836', '#161514'] : ['#5B382C', '#2E1A13'];
      let fib = '';
      for (let x = x0 + 2; x < x0 + mw - 1; x += 3.1) fib += `M${x.toFixed(1)} ${floor - back}V${(top + 1.5 + ((x * 7) % 4)).toFixed(1)}`;
      const dim = (x, y1, y2) => `<path d="M${x} ${y1}V${y2}M${x - 4} ${y1}h8M${x - 4} ${y2}h8" fill="none" stroke="#171613"/>`;
      box.innerHTML = `<svg viewBox="0 0 ${W} 122" role="img" aria-label="Section: ${T_LABEL[this.s.t]} of coir pile on a vinyl backing${well ? ', flush in a well' : `, with ${T_CLEAR[this.s.t]} needed under the door`}">
        <rect x="0" y="${floor}" width="${W}" height="8" fill="#DDD6C8"/><path d="M0 ${floor}H${W}" stroke="#171613"/>
        ${well ? `<rect x="0" y="${top}" width="${x0 - 3}" height="${t * k}" fill="#DDD6C8"/><rect x="${x0 + mw + 3}" y="${top}" width="${W}" height="${t * k}" fill="#DDD6C8"/><path d="M0 ${top}H${x0 - 3}V${floor}M${W} ${top}H${x0 + mw + 3}V${floor}" fill="none" stroke="#171613"/><path d="M${x0 - 1.5} ${top}v${t * k}M${x0 + mw + 1.5} ${top}v${t * k}" stroke="#8C8C88" stroke-width="3"/>`
          : `<path d="M${x0 + 34} 0H${x0 + mw + 30}V${door}H${x0 + 34}Z" fill="#24432F"/><path d="M${x0 + 34} ${door}H${x0 + mw + 30}" stroke="#12251A" stroke-width="2"/><text x="${x0 + 46}" y="${Math.max(16, door - 12)}" font-family="Instrument Sans, sans-serif" font-size="11" font-weight="600" letter-spacing="1.4" fill="#F3EEE2">DOOR</text>`}
        <rect x="${x0}" y="${top}" width="${mw}" height="${t * k - back}" fill="${pile[0]}"/>
        <path d="${fib}" stroke="${pile[1]}" stroke-width="1" opacity=".7"/>
        <rect x="${x0}" y="${floor - back}" width="${mw}" height="${back}" fill="#77756F"/>
        <g font-family="Instrument Sans, sans-serif" font-size="12.5" fill="#171613">
        ${dim(x0 + mw + 44, top, floor)}<text x="${x0 + mw + 54}" y="${floor - 3}"><tspan font-weight="600">${T_LABEL[this.s.t]}</tspan> mat</text>
        ${well ? `<text x="${x0 + mw + 54}" y="${top - 6}" fill="#67635A">flush with the floor</text>` : `${dim(x0 + mw + 110, door, floor)}<text x="${x0 + mw + 120}" y="${Math.min(floor - 20, door + 14)}"><tspan font-weight="600">${T_CLEAR[this.s.t]}</tspan> gap</text>`}
        <text x="0" y="118" fill="#67635A" font-size="12">${well ? 'Pile and backing, in actual proportion' : 'What the door needs to clear it, in actual proportion'}</text></g></svg>`;
    }

    // The gallery under the scene holds all six photos; the active doorway's photo leads it.
    photos(live) {
      const box = $(this.root, '[data-photos]');
      if (!box || live) return;
      $$(box, '[data-photo]').forEach((t) => t.classList.toggle('is-on', t.dataset.photo === this.s.scene));
    }

    // A tap on a photo opens it full size; arrows, the arrow keys or a swipe step through the six.
    gallery() {
      const box = $(this.root, '[data-photos]'), lb = $(this.root, '[data-glbox]');
      if (!box || !lb) return;
      const img = $(lb, 'img'), cap = $(lb, '[data-gl-cap]');
      const list = () => $$(box, '[data-photo]').sort((x, y) => y.classList.contains('is-on') - x.classList.contains('is-on'));
      let at = 0, back = null;
      const show = (n) => {
        const all = list();
        at = (n + all.length) % all.length;
        const t = all[at], small = $(t, 'img');
        img.src = t.dataset.full;
        img.alt = small ? small.alt : '';
        cap.textContent = $(t, 'span').textContent;
      };
      const close = () => { lb.hidden = true; if (back) back.focus(); };
      box.addEventListener('click', (e) => {
        const t = e.target.closest('[data-photo]');
        if (!t) return;
        back = t;
        show(list().indexOf(t));
        lb.hidden = false;
        $(lb, '[data-gl-close]').focus();
      });
      lb.addEventListener('click', (e) => {
        const step = e.target.closest('[data-gl-step]');
        if (step) return show(at + +step.dataset.glStep);
        if (e.target.closest('[data-gl-close]') || !e.target.closest('img')) close();
      });
      document.addEventListener('keydown', (e) => {
        if (lb.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight') show(at + 1);
        else if (e.key === 'ArrowLeft') show(at - 1);
      });
      let x0 = null;
      lb.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      lb.addEventListener('touchend', (e) => {
        if (x0 == null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) show(at + (dx < 0 ? 1 : -1));
      });
    }

    tween() {
      cancelAnimationFrame(this.raf);
      const from = { ...this.shown }, to = this.target, t0 = performance.now(), dur = 420;
      const step = (now) => {
        const p = clamp((now - t0) / dur, 0, 1), e = 1 - Math.pow(1 - p, 3);
        for (const k in to) this.shown[k] = from[k] + (to[k] - from[k]) * e;
        this.draw();
        if (p < 1) this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }

    draw() {
      if (!this.stage || !this.shown) return;
      const W = this.stage.clientWidth, H = this.stage.clientHeight;
      if (!W || !H) return;
      if (this.scene === 'plan') this.drawPlan(W, H);
      else this.drawScene(W, H);
    }

    drawScene(W, H) {
      const sc = SCENES[this.scene], { a, i, t } = this.shown;
      const well = this.s.recess;
      const h = well ? 0 : t;
      const P = (X, hh, d) => project(sc, X, hh, d);
      const tl = P(-a / 2, h, GAP), tr = P(a / 2, h, GAP), bl = P(-a / 2, h, GAP + i), br = P(a / 2, h, GAP + i);
      const fl = P(-a / 2, 0, GAP + i), fr = P(a / 2, 0, GAP + i);

      // Camera: the mat is the subject. Fit the mat and the width of the door, keep a
      // band of the lower door above it, and never show past the picture's edges.
      // The mat leaves a margin to grow into, so an edge can be dragged outward without a refit.
      const compact = W < 520;
      const padB = H < 220 ? 46 : Math.max(compact ? 100 : 104, H * 0.19), padSide = Math.max(compact ? 26 : 48, W * 0.12);
      const half = Math.max(fr.x - sc.mx + (well ? FRAME * sc.k0 : 0), (sc.doorW / 2 + 4) * sc.k0);
      const wallShown = (compact ? 18 : 17) * sc.k0;
      const cover = Math.max(W / sc.w, H / sc.h);
      const padT = H < 220 ? 0 : compact ? 40 : 56;       // the view and door-color buttons sit over the top band
      let z = Math.min((W / 2 - padSide) / half, (H - padB - padT) / (fl.y - (sc.y0 - wallShown)), 2.3);
      z = Math.max(z, cover);
      let tx = clamp(W / 2 - sc.mx * z, W - sc.w * z, 0);
      let ty = clamp(H - padB - fl.y * z, H - sc.h * z, 0);
      const held = this.hold && !this.hold.plan ? this.hold : null, bl0 = this.blend && !this.blend.from.plan ? this.blend : null;
      if (held) ({ z, tx, ty } = held);
      else if (bl0) {
        const f = bl0.from, e = bl0.e;
        z = f.z + (z - f.z) * e;
        tx = f.tx + (tx - f.tx) * e;
        ty = f.ty + (ty - f.ty) * e;
      }
      this.cam.style.transform = `translate(${tx}px,${ty}px) scale(${z})`;
      this.camera = { z, tx, ty };

      const mw = a * U, mh = i * U;
      this.mat.style.width = mw + 'px';
      this.mat.style.height = mh + 'px';
      this.mat.style.transform = quadMatrix(mw, mh, tl, tr, bl, br);
      const fh = Math.max(0, fl.y - bl.y);
      this.front.style.cssText = `left:${bl.x}px;top:${bl.y - 0.4}px;width:${br.x - bl.x}px;height:${fh + 0.4}px;display:${well ? 'none' : 'block'};background-size:${(16 * sc.k0 * 1.08).toFixed(1)}px 100%`;
      // soft shadow on the floor, thrown right and forward by light from the upper left
      const o = well ? 0 : 0.9 + t * 1.6;
      const sl = P(-a / 2 + o * 0.2, 0, GAP), sr = P(a / 2 + o, 0, GAP), sbl = P(-a / 2 + o * 0.2, 0, GAP + i + o * 0.7), sbr = P(a / 2 + o, 0, GAP + i + o * 0.7);
      this.shadow.style.cssText = `width:${mw}px;height:${mh}px;transform:${quadMatrix(mw, mh, sl, sr, sbl, sbr)};display:${well ? 'none' : 'block'};filter:blur(${(sc.k0 * (0.5 + t * 0.5)).toFixed(1)}px)`;
      if (well) {
        const g = FRAME;
        const wtl = P(-a / 2 - g, 0, GAP - g), wtr = P(a / 2 + g, 0, GAP - g), wbl = P(-a / 2 - g, 0, GAP + i + g), wbr = P(a / 2 + g, 0, GAP + i + g);
        const ww = (a + 2 * g) * U, wh = (i + 2 * g) * U;
        this.well.style.cssText = `display:block;width:${ww}px;height:${wh}px;transform:${quadMatrix(ww, wh, wtl, wtr, wbl, wbr)}`;
      } else this.well.style.display = 'none';

      this.plan.style.display = 'none';
      const S = (p) => ({ x: p.x * z + tx, y: p.y * z + ty });
      this.dims(W, H, S(fl), S(fr), S(tr), S(fr), S(tl));
    }

    // Plan: the mat fills the canvas, with a 1 ft grid, a scale bar and the door swings.
    drawPlan(W, H) {
      const { a, i } = this.shown;
      const well = this.s.recess;
      const compact = W < 520;
      const padX = Math.max(compact ? 30 : 70, W * 0.1), top = compact ? 66 : 92, bottom = compact ? 72 : 96;
      const sc = SCENES[sceneFor(a)] || SCENES.shopfront;
      const doorW = sc ? sc.doorW : 72, doors = Math.max(1, Math.round(doorW / 36));
      let sp = Math.min((W - 2 * padX) / Math.max(a + 8, doorW + 16), (H - top - bottom) / (i + GAP + 14), 10);
      const cx = W / 2;
      let wallY = Math.max(top, (H - (i + GAP) * sp) / 2 - 4);
      const held = this.hold && this.hold.plan ? this.hold : null, bl0 = this.blend && this.blend.from.plan ? this.blend : null;
      if (held) ({ sp, wallY } = held);
      else if (bl0) {
        sp = bl0.from.sp + (sp - bl0.from.sp) * bl0.e;
        wallY = bl0.from.wallY + (wallY - bl0.from.wallY) * bl0.e;
      }
      const x1 = cx - (a * sp) / 2, x2 = cx + (a * sp) / 2, y1 = wallY + GAP * sp, y2 = y1 + i * sp;
      const door = { green: '#24432F', navy: '#1E2A3E', oxblood: '#5E2126', clay: '#9B5B43', charcoal: '#2D2E2F' }[this.s.door];
      const p = this.plan, tile = 16 * sp;
      p.style.display = 'block';
      p.setAttribute('viewBox', `0 0 ${W} ${H}`);
      const dw = 36 * sp, dx0 = cx - (doors * dw) / 2;
      const every = sp * 12 < 9 ? 5 : sp * 12 < 26 ? 2 : 1;      // label every n feet so the numbers never collide
      let g = `<defs><pattern id="bdcoir" width="${tile}" height="${tile}" patternUnits="userSpaceOnUse"><image href="${asset('img/tex/coir-' + TEX[this.s.c] + '.webp')}" width="${tile}" height="${tile}" preserveAspectRatio="none"/></pattern>
        <pattern id="bdgrid" width="${12 * sp}" height="${12 * sp}" patternUnits="userSpaceOnUse" x="${cx}" y="${wallY}"><path d="M${12 * sp} 0H0V${12 * sp}" fill="none" stroke="#DAD3C4" stroke-width="1"/></pattern></defs>
        <rect width="${W}" height="${H}" fill="#F3EFE7"/><rect y="${wallY}" width="${W}" height="${H - wallY}" fill="url(#bdgrid)"/>
        <rect x="0" y="${wallY - 9}" width="${dx0}" height="9" fill="#2A2824"/><rect x="${dx0 + doors * dw}" y="${wallY - 9}" width="${W - dx0 - doors * dw}" height="9" fill="#2A2824"/>`;
      for (let n = 0; n < doors; n++) {
        const x = dx0 + n * dw, hingeLeft = doors === 1 || n % 2 === 0;
        const hx = hingeLeft ? x : x + dw, ex = hingeLeft ? x + dw : x;
        g += `<rect x="${x + 1}" y="${wallY - 7}" width="${dw - 2}" height="5" rx="1" fill="${door}"/>
          <path d="M${ex} ${wallY - 9}A${dw} ${dw} 0 0 ${hingeLeft ? 0 : 1} ${hx} ${wallY - 9 - Math.min(dw, wallY - 20)}" fill="none" stroke="#A39C8C" stroke-width="1" stroke-dasharray="3 4"/>`;
      }
      g += `<g font-family="Instrument Sans, sans-serif" font-size="11" fill="#67635A" text-anchor="middle">`;
      for (let ft = every; ft * 12 * sp < W / 2 - 14; ft += every) g += `<text x="${cx + ft * 12 * sp}" y="${wallY + 13}">${ft}′</text><text x="${cx - ft * 12 * sp}" y="${wallY + 13}">${ft}′</text>`;
      g += `</g>`;
      if (well) g += `<rect x="${x1 - 5}" y="${y1 - 5}" width="${x2 - x1 + 10}" height="${y2 - y1 + 10}" fill="#C4C3BE" stroke="#77736A" stroke-width="1"/><rect x="${x1 - 1.5}" y="${y1 - 1.5}" width="${x2 - x1 + 3}" height="${y2 - y1 + 3}" fill="#2A2824"/>`;
      else g += `<rect x="${x1 + 3}" y="${y1 + 4}" width="${x2 - x1}" height="${y2 - y1}" fill="rgba(40,30,16,.16)"/>`;
      g += `<rect x="${x1}" y="${y1}" width="${x2 - x1}" height="${y2 - y1}" fill="url(#bdcoir)" stroke="rgba(60,40,10,.55)" stroke-width="1"/>`;
      const bar = every * 12 * sp;
      g += `<g transform="translate(${compact ? 14 : 20},${H - (compact ? 16 : 22)})"><path d="M0 0H${bar}M0 -4V4M${bar} -4V4" stroke="#171613" fill="none"/><text x="${bar + 8}" y="4" font-family="Instrument Sans, sans-serif" font-size="11.5" fill="#171613">${every} ft</text></g>`;
      p.innerHTML = g;
      this.camera = { plan: true, sp, cx, wallY };
      this.dims(W, H, { x: x1, y: y2 }, { x: x2, y: y2 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y1 });
    }

    // Dimension lines, tags and grips in stage pixels: the width runs fl->fr along the door, the length tr->br
    // toward it. Each line is drawn in its slider's color, with arrowheads and short extension lines.
    dims(W, H, fl, fr, tr, br, tl) {
      const L = this.lines;
      L.setAttribute('viewBox', `0 0 ${W} ${H}`);
      const oa = 15, tick = 6, head = 7;
      const ay = Math.min(fl.y + oa, H - 30);
      const dx = br.x - tr.x, dy = br.y - tr.y, len = Math.hypot(dx, dy) || 1;
      const ux = dx / len, uy = dy / len, nx = dy / len, ny = -dx / len;
      const i1 = { x: tr.x + nx * oa, y: tr.y + ny * oa }, i2 = { x: br.x + nx * oa, y: br.y + ny * oa };
      // an arrow from p to q with heads at both ends; (ex, ey) is the unit vector from p to q
      const arrow = (p, q, ex, ey) => {
        const px = -ey, py = ex, h = Math.min(head, Math.hypot(q.x - p.x, q.y - p.y) / 3);
        const tip = (o, sgn) => `M${o.x + sgn * ex * h + px * h * .55} ${o.y + sgn * ey * h + py * h * .55}L${o.x} ${o.y}L${o.x + sgn * ex * h - px * h * .55} ${o.y + sgn * ey * h - py * h * .55}`;
        return `M${p.x} ${p.y}L${q.x} ${q.y}${tip(p, 1)}${tip(q, -1)}`;
      };
      const w = arrow({ x: fl.x, y: ay }, { x: fr.x, y: ay }, 1, 0) + `M${fl.x} ${fl.y + 3}V${ay + tick}M${fr.x} ${fr.y + 3}V${ay + tick}`;
      const l = arrow(i1, i2, ux, uy) + `M${tr.x + nx * 3} ${tr.y + ny * 3}L${i1.x + nx * tick} ${i1.y + ny * tick}M${br.x + nx * 3} ${br.y + ny * 3}L${i2.x + nx * tick} ${i2.y + ny * tick}`;
      const line = (d, cls) => `<g class="${cls}"><path class="bd-halo" d="${d}"/><path class="bd-ink" d="${d}"/></g>`;
      L.innerHTML = `<g fill="none" stroke-linecap="round" stroke-linejoin="round">${line(w, 'bd-dim bd-dim--w')}${line(l, 'bd-dim bd-dim--l')}</g>`;
      const place = (tag, x, y, ax, ay2) => {
        const w = tag.offsetWidth, h = tag.offsetHeight;
        tag.style.transform = `translate(${Math.round(clamp(x - w * ax, 6, W - w - 6))}px,${Math.round(clamp(y - h * ay2, 6, H - h - 6))}px)`;
      };
      place(this.tagA, (fl.x + fr.x) / 2, ay + 9, 0.5, 0);
      const right = (i1.x + i2.x) / 2 + 12;
      if (right + this.tagI.offsetWidth > W - 4) place(this.tagI, (tr.x + br.x) / 2 - 16, (tr.y + br.y) / 2, 1, 0.5);   // no room outside: sit on the mat
      else place(this.tagI, right, (i1.y + i2.y) / 2, 0, 0.5);
      // each drag zone covers its whole edge, at least 48 px across, with the corners on top
      const zone = (h, x1, y1, x2, y2, pad) => {
        const w = Math.max(48, Math.abs(x2 - x1) + 2 * pad), hh = Math.max(48, Math.abs(y2 - y1));
        h.style.width = w + 'px';
        h.style.height = hh + 'px';
        h.style.transform = `translate(${Math.round((x1 + x2) / 2 - w / 2)}px,${Math.round((y1 + y2) / 2 - hh / 2)}px)`;
      };
      const [gl, gr, gf, gfl, gfr] = this.handles;
      zone(gl, tl.x, tl.y, fl.x, fl.y, 20);
      zone(gr, tr.x, tr.y, fr.x, fr.y, 20);
      zone(gf, fl.x + 26, fl.y, fr.x - 26, fl.y, 0);
      zone(gfl, fl.x, fl.y - 28, fl.x, fl.y + 28, 28);
      zone(gfr, fr.x, fr.y - 28, fr.x, fr.y + 28, 28);
    }
  }

  window.CoirBuilder = { Builder, priceFor, parseSize, fmt, money, RATES, Itoris, charged };
  const start = () => document.querySelectorAll('[data-builder]').forEach((r) => (r.builder = new Builder(r)));
  // site.js (the shipping estimate) loads after this file; both are deferred, so wait for the page to be ready
  if (document.readyState === 'loading' || !window.CoirShip) document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
